import { useMantineTheme } from '@mantine/core';
import { trackEvent } from '@repo/common/analytics';
import RichTooltip from '@repo/common/components/RichTooltip/RichTooltip';
import { EntityType } from '@repo/common/enums/entityType';
import { Text } from '@repo/foundations';
import { Icon } from '@repo/foundations/components/Icon';
import { useMount } from 'ahooks';
import { isNil } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import {
	useAuthUser,
	useIntegrationPrefetched,
	usePolicyEnabled,
	useQuestionsEnabled,
	useSuspenseFeatureAccess,
	useSuspenseSchema,
	useUpdateSchema,
} from '../../api';
import { EntityAccessControlList } from '../../components/AccessControl/EntityAccessControlList';
import Documentation from '../../components/Documentation';
import EntityDiscussions from '../../components/EntityDiscussions';
import EntityLogo from '../../components/EntityLogo/EntityLogo';
import EntityPageLayout from '../../components/EntityPageLayout';
import EntityPageLayoutSkeleton from '../../components/EntityPageLayout/EntityPageLayoutSkeleton';
import { StaticProperty } from '../../components/EntityPageLayout/EntityPropertySidebar';
import { useLineageTab } from '../../components/EntityPageLayout/hooks';
import {
	getDefaultTab,
	getOrderedTabs,
} from '../../components/EntityPageLayout/utils';
import EntityPolicyList from '../../components/EntityPolicyList/EntityPolicyList';
import { LineageTab } from '../../components/LineageGraph/LineageTab';
import type { OverviewPageTab } from '../../components/Overview/OverviewPageTabs';
import OverviewPageTabs from '../../components/Overview/OverviewPageTabs';
import { useFeatureFlags } from '../../utils/featureFlags';
import { useTabNavigate } from '../../utils/hook/useTabNavigate';
import { useParamsIdSuffixUuid } from '../../utils/hook/utils';
import type { DjangoValueType } from '../TemplatePage/types';
import TablesTab from './components/TablesTab';

const InnerSchemaPage = observer(({ id }: { id: string }) => {
	const { user, workspace } = useAuthUser();
	const hasPolicyAccess = usePolicyEnabled();
	const questionsEnabled = useQuestionsEnabled();
	const { entityPrivilegesTab } = useFeatureFlags();
	const navigateToTab = useTabNavigate();
	const theme = useMantineTheme();
	const { lineageAccess, secodaCoreAccess } = useSuspenseFeatureAccess();
	const { data: schema } = useSuspenseSchema({
		id,
	});

	const { data: integration } = useIntegrationPrefetched({
		id: schema.integration!,
		options: {
			enabled: !isNil(schema.integration),
		},
	});

	useMount(() => {
		trackEvent('schema/open', {}, user, workspace);
	});

	const { mutateAsync } = useUpdateSchema({});

	// Debouncing this is not a good idea, because it overwrites changes, does not
	// queue them.
	const updateSchema = useCallback(
		async (key: string, value: DjangoValueType, saveRemotely = true) => {
			if (saveRemotely) {
				mutateAsync({
					data: {
						id: schema!.id,
						[key]: value,
					},
				});
				trackEvent('schema/properties/update', {}, user, workspace!);
			}
		},
		[mutateAsync, schema, user, workspace]
	);

	const { lineageFlowStore, lineageFlowUIStore, lineageFilterStore } =
		useLineageTab(
			schema.id,
			schema.published,
			EntityType.schema,
			schema.native_type
		);

	if (!schema) {
		return <EntityPageLayoutSkeleton />;
	}

	const documentationFirst = (schema.definition?.length || 0) > 2;

	const tabs: OverviewPageTab[] = useMemo(
		() => [
			{
				value: 'tables',
				label: 'Tables',
				component: <TablesTab schemaId={schema.id} />,
			},
			{
				value: 'lineage',
				label: 'Lineage',
				component: (
					<LineageTab
						lineageFlowStore={lineageFlowStore}
						lineageFlowUIStore={lineageFlowUIStore}
						lineageFilterStore={lineageFilterStore}
					/>
				),
				condition: integration?.type === 'powerbi' && !!lineageAccess,
			},
			{
				value: 'discussions',
				label: 'Questions',
				condition: questionsEnabled && !!secodaCoreAccess,
				component: <EntityDiscussions entity={schema} />,
			},
			{
				value: 'documentation',
				label: 'Documentation',
				component: <Documentation entity={schema} />,
			},
			{
				value: 'privileges',
				label: 'Privileges',
				component: <EntityAccessControlList entity={schema} />,
				condition: entityPrivilegesTab,
			},
			{
				value: 'policies',
				label: 'Policies',
				component: <EntityPolicyList entity={schema} />,
				condition: hasPolicyAccess,
			},
		],
		[
			schema,
			integration?.type,
			lineageAccess,
			entityPrivilegesTab,
			hasPolicyAccess,
			secodaCoreAccess,
			questionsEnabled,
		]
	);

	const orderedTabs: OverviewPageTab[] = useMemo(
		() => getOrderedTabs(tabs, documentationFirst),
		[tabs, documentationFirst]
	);

	const { tab: urlTabParam } = useParams();

	const defaultTab = useMemo(() => getDefaultTab(orderedTabs), [orderedTabs]);
	const currentTab = urlTabParam ?? defaultTab;

	return (
		<EntityPageLayout
			key={schema.id}
			name="Schema"
			isReadOnlyTitle
			entity={schema}
			icon={<EntityLogo size={32} entity={schema} />}
			updateEntity={updateSchema}
			withDescription
			withFrequentUsers
			withOwnerSelector
			withCollectionSelector
			withCustomPropertyEditors
			withTagSelector
			withEntityPopularity
			withRelatedResourceSelector
			withCustomPublishElement={
				<RichTooltip
					title="Read-only"
					body={<Text size="sm">Schemas are always published.</Text>}
					position="left"
				>
					<StaticProperty
						type="badge"
						label="Status"
						value={{
							label: 'Published',
							value: true,
							icon: <Icon name="circleCheckFilled" />,
							iconColor: theme.other.getColor('icon/success/default'),
							fill: 'surface/primary/disabled',
						}}
					/>
				</RichTooltip>
			}
		>
			<OverviewPageTabs
				key={schema.id}
				defaultTab={defaultTab}
				currentTab={currentTab}
				tabs={orderedTabs}
				onTabChange={navigateToTab}
			/>
		</EntityPageLayout>
	);
});

// by memoizing the inner component, we can avoid re-rendering the entire page on tab changes and only re-render the tab panel
export default function SchemaPage() {
	const id = useParamsIdSuffixUuid();

	return <InnerSchemaPage id={id} />;
}
